'use client';

import React from 'react';
import Image from 'next/image';
import styles from './Step1Industry.module.css';

interface Industry {
  key: string;
  title: string;
  image: string;
}

interface Step1IndustryProps {
  value: string;
  onChange: (value: string) => void;
  onAutoAdvance?: () => void;
  error?: string;
  translations: any;
}

export default function Step1Industry({ value, onChange, onAutoAdvance, error, translations }: Step1IndustryProps) {
  const t = translations.demoRequest?.form || translations.form || {};
  
  const handleSelection = (key: string) => {
    onChange(key);
    // Auto-advance to next step after a short delay
    // We pass the value directly to avoid state timing issues
    if (onAutoAdvance) {
      setTimeout(() => {
        onAutoAdvance();
      }, 300); // Small delay so user sees the selection
    }
  };
  
  // All industries with their images (matching /branches page)
  const industries: Industry[] = [
    { key: 'hotels', title: t.businessTypes?.hotels || 'Hotels', image: '/images/venues/hotels.webp' },
    { key: 'beachclubs', title: t.businessTypes?.beachclubs || 'Beachclubs', image: '/images/venues/beach.webp' },
    { key: 'events', title: t.businessTypes?.events || 'Events & Festivals', image: '/images/venues/events.webp' },
    { key: 'restaurants', title: t.businessTypes?.restaurants || 'Restaurants', image: '/images/venues/restaurant.webp' },
    { key: 'cafes', title: t.businessTypes?.cafes || 'Cafés & Bars', image: '/images/venues/cafe.webp' },
    { key: 'bowling', title: t.businessTypes?.bowling || 'Bowling Centers', image: '/images/venues/bowling.webp' },
    { key: 'zoos', title: t.businessTypes?.zoos || 'Zoos', image: '/images/venues/zoo.webp' },
    { key: 'golf', title: t.businessTypes?.golf || 'Golf Courses', image: '/images/venues/golf.webp' },
    { key: 'stadiums', title: t.businessTypes?.stadiums || 'Stadiums', image: '/images/venues/stadium.webp' },
    { key: 'fastfood', title: t.businessTypes?.fastfood || 'Fast Food', image: '/images/venues/fastfood.webp' },
    { key: 'foodhalls', title: t.businessTypes?.foodhalls || 'Food Halls', image: '/images/venues/foodhall.webp' },
    { key: 'allyoucaneat', title: t.businessTypes?.allyoucaneat || 'All You Can Eat', image: '/images/venues/buffet.webp' },
    { key: 'lunchrooms', title: t.businessTypes?.lunchrooms || 'Lunchrooms', image: '/images/venues/lunchroom.webp' },
    { key: 'bakeries', title: t.businessTypes?.bakeries || 'Bakeries', image: '/images/venues/bakery.webp' },
    { key: 'catering', title: t.businessTypes?.catering || 'Catering', image: '/images/venues/catering.webp' },
    { key: 'cruise', title: t.businessTypes?.cruise || 'Cruise Ships', image: '/images/venues/cruise.webp' },
    { key: 'casino', title: t.businessTypes?.casino || 'Casinos', image: '/images/venues/casino.webp' },
    { key: 'spa', title: t.businessTypes?.spa || 'Spa & Wellness', image: '/images/venues/spa.webp' },
    { key: 'healthcare', title: t.businessTypes?.healthcare || 'Healthcare', image: '/images/venues/hospitals.webp' },
    { key: 'airports', title: t.businessTypes?.airports || 'Airports', image: '/images/venues/airports.webp' },
    { key: 'museums', title: t.businessTypes?.museums || 'Museums', image: '/images/venues/museum.webp' },
    { key: 'congress', title: t.businessTypes?.congress || 'Congress Centers', image: '/images/venues/congress.webp' },
    { key: 'concerthalls', title: t.businessTypes?.concerthalls || 'Concert Halls', image: '/images/venues/concert.webp' },
    { key: 'theaters', title: t.businessTypes?.theaters || 'Theaters', image: '/images/venues/theaters.webp' },
    { key: 'ferries', title: t.businessTypes?.ferries || 'Ferries', image: '/images/venues/ferry.webp' },
    { key: 'expo', title: t.businessTypes?.expo || 'Expos', image: '/images/venues/expo.webp' },
    { key: 'canteen', title: t.businessTypes?.canteen || 'Canteens', image: '/images/venues/canteen.webp' },
    { key: 'university', title: t.businessTypes?.university || 'Universities', image: '/images/venues/university.webp' },
    { key: 'amusementpark', title: t.businessTypes?.amusementpark || 'Amusement Parks', image: '/images/venues/amusement.webp' },
    { key: 'foodtruck', title: t.businessTypes?.foodtruck || 'Food Trucks', image: '/images/venues/foodtruck.webp' },
    { key: 'holidayparks', title: t.businessTypes?.holidayparks || 'Holiday Parks', image: '/images/venues/parks.webp' },
    { key: 'icecream', title: t.businessTypes?.icecream || 'Ice Cream Shops', image: '/images/venues/icecream.webp' },
  ];

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t.step1?.title || 'Wat voor type bedrijf heeft u?'}</h2>
      <p className={styles.subtitle}>{t.step1?.subtitle || 'Selecteer de branche die het beste bij uw bedrijf past'}</p>
      
      <div className={styles.grid}>
        {industries.map((industry) => (
          <button
            key={industry.key}
            type="button"
            className={`${styles.card} ${value === industry.key ? styles.active : ''}`}
            onClick={() => handleSelection(industry.key)}
          >
            <div className={styles.imageWrapper}>
              <Image
                src={industry.image}
                alt={industry.title}
                fill
                sizes="(max-width: 768px) 150px, 200px"
                className={styles.image}
              />
              <div className={styles.overlay} />
            </div>
            <span className={styles.label}>{industry.title}</span>
          </button>
        ))}
      </div>
      
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}

